import React, {Children, isValidElement, useEffect, useState} from 'react';
import {Box, Checkbox, FormControlLabel, IconButton, Popover, Tooltip} from '@mui/material';
import {ViewColumn} from '@mui/icons-material';
import LocalStorageService from 'services/LocalStorageService';

export type ColumnVisibilityProps = {
  /** Start hidden. The column stays available from the columns menu. */
  defaultHidden?: boolean;
  /** Set false to keep the column always visible and out of the columns menu. */
  hideable?: boolean;
};

type ColumnChildProps = ColumnVisibilityProps & {
  source?: unknown;
  label?: unknown;
};

export type ListColumnMeta = {
  index: number;
  id: string;
  label: string;
  defaultHidden: boolean;
  hideable: boolean;
};

type ColumnVisibilityContextValue = {
  columns: ListColumnMeta[];
  overrides: Record<string, boolean>;
  toggle: (id: string, hidden: boolean) => void;
};

const ColumnVisibilityContext = React.createContext<ColumnVisibilityContextValue | null>(null);

export const useColumnVisibility = () => React.useContext(ColumnVisibilityContext);

export const describeColumns = (children: React.ReactNode): ListColumnMeta[] => {
  const elements = Children.toArray(children).filter(isValidElement);
  const used = new Map<string, number>();

  return elements.map((element, index) => {
    const props = element.props as ColumnChildProps;
    const source = typeof props.source === 'string' ? props.source : undefined;
    const label = typeof props.label === 'string' && props.label ? props.label : undefined;
    const base = source || (label ? `label:${label}` : `index:${index}`);
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);

    return {
      index,
      id: seen === 0 ? base : `${base}#${seen}`,
      label: label || source || `Column ${index + 1}`,
      defaultHidden: Boolean(props.defaultHidden),
      hideable: props.hideable !== false && Boolean(label || source)
    };
  });
};

export const isColumnHidden = (column: ListColumnMeta | undefined, overrides: Record<string, boolean>) => {
  if (!column || !column.hideable) {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(overrides, column.id)) {
    return overrides[column.id];
  }
  return column.defaultHidden;
};

export const visibleColumnElements = (children: React.ReactNode, columns: ListColumnMeta[], overrides: Record<string, boolean>) => {
  return Children.toArray(children)
    .filter(isValidElement)
    .filter((_, index) => !isColumnHidden(columns[index], overrides));
};

export const ColumnVisibilityProvider = ({
  columns,
  storageKey,
  children
}: {
  columns: React.ReactNode;
  storageKey: string;
  children: React.ReactNode;
}) => {
  const described = describeColumns(columns);
  const [overrides, setOverrides] = useState<Record<string, boolean>>(() => LocalStorageService.getListColumnVisibility(storageKey));

  useEffect(() => {
    setOverrides(LocalStorageService.getListColumnVisibility(storageKey));
  }, [storageKey]);

  const toggle = (id: string, hidden: boolean) => {
    setOverrides((current) => {
      const next = {...current, [id]: !hidden};
      LocalStorageService.setListColumnVisibility(storageKey, next);
      return next;
    });
  };

  return <ColumnVisibilityContext.Provider value={{columns: described, overrides, toggle}}>{children}</ColumnVisibilityContext.Provider>;
};

export const ColumnVisibilityMenu = () => {
  const visibility = useColumnVisibility();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  if (!visibility) {
    return null;
  }

  const hideable = visibility.columns.filter((column) => column.hideable);
  if (hideable.length === 0) {
    return null;
  }

  const hasHidden = hideable.some((column) => isColumnHidden(column, visibility.overrides));

  return (
    <>
      <Tooltip title="Columns">
        <IconButton aria-label="Show or hide columns" color={hasHidden ? 'primary' : 'default'} onClick={(event) => setAnchor(event.currentTarget)}>
          <ViewColumn />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', py: 1, minWidth: 200}}>
          {hideable.map((column) => {
            const hidden = isColumnHidden(column, visibility.overrides);
            return (
              <FormControlLabel
                key={column.id}
                label={column.label}
                sx={{mx: 0, px: 1.5, py: 0.25, '&:hover': {bgcolor: 'action.hover'}}}
                control={<Checkbox size="small" checked={!hidden} onChange={() => visibility.toggle(column.id, hidden)} />}
              />
            );
          })}
        </Box>
      </Popover>
    </>
  );
};
